import { useRef, useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ArrowRight,
  Star,
  Coffee,
  ShoppingBag,
  BedDouble,
  Users,
  Flag,
  Percent,
  Gift,
  CheckCircle,
  Crown,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const amenities = [
  {
    title: "Pro Shop",
    image:
      "https://res.cloudinary.com/dxdvcplbl/image/upload/v1790005019/proshop_thumb_cy4zly.jpg",
    icon: <ShoppingBag className="w-6 h-6" />,
    description: "Premium golf equipment and accessories",
    link: "/pro-shop",
    color: "from-emerald-500 to-emerald-400",
    bg: "bg-emerald-50",
    border: "border-emerald-100",
  },
  {
    title: "Restaurant",
    image:
      "https://res.cloudinary.com/dxdvcplbl/image/upload/v1758112819/WhatsApp_Image_2025-09-17_at_5.57.59_PM-min_trljoi.jpg",
    icon: <Coffee className="w-6 h-6" />,
    description: "Fine dining with scenic views",
    link: "/restaurant",
    color: "from-amber-500 to-amber-400",
    bg: "bg-amber-50",
    border: "border-amber-100",
  },
  {
    title: "Golf Retreat",
    image:
      "https://res.cloudinary.com/dxdvcplbl/image/upload/v1789983320/guest_retreat_2_giu1mm.png",
    icon: <BedDouble className="w-6 h-6" />,
    description: "Luxurious accommodation for members",
    link: "/guest-room",
    color: "from-blue-500 to-blue-400",
    bg: "bg-blue-50",
    border: "border-blue-100",
  },
  {
    title: "Convention Hall",
    image:
      "https://res.cloudinary.com/dxdvcplbl/image/upload/v1789981514/convention_hall_2_mzqaex.png",
    icon: <Users className="w-6 h-6" />,
    description: "Elegant venue for events and meetings",
    link: "/convention-hall",
    color: "from-purple-500 to-purple-400",
    bg: "bg-purple-50",
    border: "border-purple-100",
  },
];

const guestFacilities = [
  {
    id: 1,
    title: "Golf View Restaurant",
    desc: "Guests can avail facilities at the Golf View Restaurant and enjoy premium dining in a serene atmosphere.",
    icon: <Coffee className="w-6 h-6" />,
    features: ["Premium dining", "Riverside views", "Fine cuisine"],
  },
  {
    id: 2,
    title: "Play Golf with Fair Fee",
    desc: "Guests are welcome to play golf with a fair fee, experiencing the beautifully maintained course.",
    icon: <Flag className="w-6 h-6" />,
    features: ["9-hole course", "Equipment rental", "Professional guidance"],
  },
  {
    id: 3,
    title: "Guest Room Facilities",
    desc: "Guests can avail guest room facilities for a comfortable and convenient stay within the club premises.",
    icon: <BedDouble className="w-6 h-6" />,
    features: ["Luxury rooms", "Room service", "Scenic views"],
  },
  {
    id: 4,
    title: "Pro Shop Facilities",
    desc: "Guests can access the Pro Shop facilities for golfing essentials and accessories.",
    icon: <ShoppingBag className="w-6 h-6" />,
    features: ["Premium brands", "Expert advice", "Latest equipment"],
  },
];

const memberFacilities = [
  {
    id: 1,
    title: "Discount at Golf View Restaurant",
    desc: "Members can avail discount at the Golf View Restaurant, making dining more enjoyable and affordable.",
    icon: <Percent className="w-6 h-6" />,
    discount: "25% OFF",
    features: [
      "Member-only pricing",
      "Priority reservations",
      "Seasonal offers",
    ],
  },
  {
    id: 2,
    title: "Guest Room Facilities",
    desc: "Members have access to guest room facilities for a comfortable stay within the club premises.",
    icon: <BedDouble className="w-6 h-6" />,
    discount: "Complimentary Access",
    features: ["Priority booking", "Family stays", "Extended hours"],
  },
  {
    id: 3,
    title: "Golf without Fair Fee",
    desc: "Members can enjoy playing golf without a fair and reduced fee, encouraging more participation.",
    icon: <Flag className="w-6 h-6" />,
    discount: "No Green Fees",
    features: ["Unlimited access", "Priority tee times", "Member tournaments"],
  },
  {
    id: 4,
    title: "Discount at Pro Shop",
    desc: "Members can avail discounts at the Pro Shop on golfing essentials and accessories.",
    icon: <Gift className="w-6 h-6" />,
    discount: "20% OFF",
    features: ["Exclusive brands", "Member previews", "Seasonal sales"],
  },
];

const Facilities = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const checkScrollPosition = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);

      const itemWidth = scrollRef.current.children[0]?.offsetWidth || 0;
      const scrollIndex = Math.round(scrollLeft / itemWidth);
      setActiveIndex(scrollIndex);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const scrollToIndex = (index) => {
    if (scrollRef.current) {
      const itemWidth = scrollRef.current.children[0]?.offsetWidth || 0;
      scrollRef.current.scrollTo({
        left: itemWidth * index,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const currentRef = scrollRef.current;
    currentRef?.addEventListener("scroll", checkScrollPosition);
    window.addEventListener("resize", checkScrollPosition);
    checkScrollPosition();

    return () => {
      currentRef?.removeEventListener("scroll", checkScrollPosition);
      window.removeEventListener("resize", checkScrollPosition);
    };
  }, []);

  return (
    <div className="bg-gradient-to-br from-emerald-50 via-white to-lime-50">
      {/* Hero */}
      <section className="py-16 md:py-24 px-4 md:px-8 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-emerald-100 rounded-full opacity-20 blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-lime-100 rounded-full opacity-20 blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-lime-400 rounded-full blur-lg opacity-30"></div>
                <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-emerald-50 to-lime-50 border-2 border-emerald-100 flex items-center justify-center shadow-lg">
                  <Star className="w-10 h-10 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3 mb-4">
              <Sparkles className="w-5 h-5 text-emerald-500" />
              <p className="text-sm font-semibold tracking-widest text-emerald-600 uppercase">
                Premium Amenities
              </p>
              <Sparkles className="w-5 h-5 text-emerald-500" />
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              Our Exclusive{" "}
              <span className="bg-gradient-to-r from-emerald-600 to-lime-600 bg-clip-text text-transparent">
                Facilities
              </span>
            </h1>

            <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto">
              Experience unparalleled luxury and convenience with our
              world-class amenities, available to guests and members alike
            </p>
          </div>

          {/* Amenities Slider */}
          <div className="relative">
            {showLeftArrow && (
              <button
                onClick={() => scroll("left")}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 md:-translate-x-8 bg-white/90 backdrop-blur-sm shadow-2xl rounded-full p-3 z-20 hover:bg-white transition-all duration-300 group hover:scale-110 border border-gray-200"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-6 h-6 text-gray-700 group-hover:text-emerald-600 transition-colors" />
              </button>
            )}

            <div
              ref={scrollRef}
              className="flex overflow-x-auto gap-8 pb-8 px-4 scrollbar-hide scroll-smooth"
              style={{ scrollBehavior: "smooth" }}
            >
              {amenities.map((amenity, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-80 md:w-96 group"
                  onClick={() => scrollToIndex(index)}
                >
                  <div
                    className={`bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-2 ${
                      index === activeIndex
                        ? "border-emerald-300"
                        : "border-gray-100"
                    } overflow-hidden cursor-pointer h-full flex flex-col`}
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={amenity.image}
                        alt={amenity.title}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/30 via-transparent to-transparent"></div>
                      <div className="absolute top-4 left-4">
                        <div
                          className={`w-12 h-12 rounded-xl ${amenity.bg} ${amenity.border} flex items-center justify-center shadow-lg`}
                        >
                          <div className="text-emerald-600">{amenity.icon}</div>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 flex-1 flex flex-col">
                      <div className="mb-4">
                        <div
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-3 ${amenity.bg} ${amenity.border}`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full bg-gradient-to-r ${amenity.color}`}
                          ></div>
                          Premium Amenity
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {amenity.title}
                        </h3>
                        <p className="text-gray-600">{amenity.description}</p>
                      </div>

                      <div className="mt-auto pt-4 border-t border-gray-100">
                        <Link
                          to={amenity.link}
                          className="group/link inline-flex items-center justify-between w-full"
                        >
                          <span className="text-emerald-600 hover:text-emerald-800 font-semibold text-lg group-hover/link:underline transition-all duration-300">
                            Explore {amenity.title}
                          </span>
                          <span className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center group-hover/link:bg-emerald-100 transition-colors duration-300">
                            <ArrowRight className="w-4 h-4 text-emerald-600 group-hover/link:translate-x-1 transition-transform duration-300" />
                          </span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {showRightArrow && (
              <button
                onClick={() => scroll("right")}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 md:translate-x-8 bg-white/90 backdrop-blur-sm shadow-2xl rounded-full p-3 z-20 hover:bg-white transition-all duration-300 group hover:scale-110 border border-gray-200"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-6 h-6 text-gray-700 group-hover:text-emerald-600 transition-colors" />
              </button>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 mt-4">
            {amenities.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === activeIndex
                    ? "w-8 bg-gradient-to-r from-emerald-500 to-lime-500"
                    : "bg-gray-300 hover:bg-emerald-400"
                }`}
                aria-label={`Go to amenity ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* For Guests */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm font-semibold mb-4">
              <Users className="w-4 h-4" />
              For Guests
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Facilities for Guests
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Visiting RGCC? Here's what you can enjoy at fair, guest-friendly
              rates.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {guestFacilities.map((facility) => (
              <div
                key={facility.id}
                className="bg-gradient-to-br from-emerald-50 to-lime-50 border border-emerald-100 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center mb-4 shadow-sm text-emerald-600">
                  {facility.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {facility.title}
                </h3>
                <p className="text-gray-600 mb-4">{facility.desc}</p>
                <ul className="space-y-2">
                  {facility.features.map((feature, idx) => (
                    <li
                      key={idx}
                      className="flex items-center gap-2 text-sm text-gray-600"
                    >
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Members */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-gradient-to-br from-amber-50 via-white to-emerald-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-semibold mb-4">
              <Crown className="w-4 h-4" />
              For Members
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Facilities for Members
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Membership unlocks exclusive discounts and priority access across
              the club.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {memberFacilities.map((facility) => (
              <div
                key={facility.id}
                className="relative bg-white border border-amber-100 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              >
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold shadow">
                  {facility.discount}
                </div>
                <div className="w-14 h-14 rounded-xl bg-amber-50 flex items-center justify-center mb-4 text-amber-600">
                  {facility.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 pr-24">
                  {facility.title}
                </h3>
                <p className="text-gray-600 mb-4">{facility.desc}</p>
                <ul className="space-y-2">
                  {facility.features.map((feature, idx) => (
                    <li
                      key={idx}
                      className="flex items-center gap-2 text-sm text-gray-600"
                    >
                      <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 md:px-8 pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto bg-gradient-to-r from-emerald-500 to-lime-500 rounded-2xl p-8 md:p-12 text-center text-white">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Access Every Facility?
          </h3>
          <p className="mb-6 max-w-2xl mx-auto text-emerald-100">
            Become a member to unlock discounts and priority access on top of
            everything guests already enjoy.
          </p>
          <button
            onClick={() => navigate("/membership")}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold bg-white text-emerald-600 hover:bg-emerald-50 hover:scale-105 transition-all duration-300 shadow-lg"
          >
            <span>Become a Member</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default Facilities;
